import React, { useEffect, useRef, useState } from "react";
import Button from "../../../../CommonElements/Button";
import Container from "../../../../CommonElements/Container";
import SectionHeading from "../../../../CommonElements/SectionHeading";
import OutgoingArrow from "../../../../CommonElements/OutgoingArrow";
import { STEPS } from "../../../../Data/steps";
import { useLanguage } from "../../../../context/LanguageContext";
import { useSignupModal } from "../../../../context/SignupModalContext";
import useReveal from "../../../../hooks/useReveal";
import arrowCard from "../../../../assets/images/arrow-card.svg";

const Steps = () => {
  const scrollerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const { openSignup } = useSignupModal();
  const { t } = useLanguage();
  const [revealRef, inView] = useReveal();
  const stepCopy = Array.isArray(t("steps.items")) ? t("steps.items") : [];

  const updateActiveFromScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const card = scroller.querySelector(".card--step");
    if (!card) return;

    const gap = 24;
    const stepSize = card.getBoundingClientRect().width + gap;
    const index = Math.round(scroller.scrollLeft / stepSize);
    setActiveIndex(Math.max(0, Math.min(index, STEPS.length - 1)));
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    updateActiveFromScroll();
    scroller.addEventListener("scroll", updateActiveFromScroll, {
      passive: true,
    });
    return () => scroller.removeEventListener("scroll", updateActiveFromScroll);
  }, []);

  const scrollByCard = (direction = 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const card = scroller.querySelector(".card--step");
    if (!card) return;

    const gap = 24;
    const stepSize = card.getBoundingClientRect().width + gap;
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const nextLeft = scroller.scrollLeft + direction * stepSize;

    // Loop to start when advancing past the end
    if (direction > 0 && scroller.scrollLeft >= maxScroll - 4) {
      scroller.scrollTo({ left: 0, behavior: "smooth" });
      return;
    }

    scroller.scrollTo({
      left: Math.max(0, Math.min(nextLeft, maxScroll)),
      behavior: "smooth",
    });
  };

  return (
    <section className={`section steps${inView ? " is-in" : ""}`} id="steps" ref={revealRef}>
      <Container className="steps__content">
        <SectionHeading
          className="steps__heading"
          title={t("steps.title")}
          align="center"
          light
        />

        <div className="steps__cards-area">
          <div className="steps__floor" aria-hidden="true">
            <span className="steps__floor-road" />
          </div>

          <div className="steps__scroller" ref={scrollerRef} dir="ltr">
            <div className="steps__track">
              {STEPS.map((step, index) => (
                <article
                  className={
                    index === activeIndex
                      ? "card card--step card--step-active"
                      : "card card--step"
                  }
                  key={step.id}
                >
                  <h3 className="card__title">
                    {stepCopy[index]?.title || step.title}
                  </h3>
                  <p className="card__text">
                    {stepCopy[index]?.description || step.description}
                  </p>
                  <button
                    type="button"
                    className="steps__arrow"
                    onClick={() => scrollByCard(1)}
                    aria-label={t("steps.nextStep")}
                  >
                    <img
                      src={arrowCard}
                      alt=""
                      width={34}
                      height={18}
                      aria-hidden="true"
                    />
                  </button>
                  <span className="card__watermark" aria-hidden="true">
                    {step.number}
                  </span>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="steps__cta">
          <Button
            variant="primary"
            size="lg"
            icon={<OutgoingArrow size={18} />}
            onClick={openSignup}
          >
            {t("hero.participateNow")}
          </Button>
        </div>
      </Container>
    </section>
  );
};

export default Steps;
